import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Trans } from '@/UI/Trans/Trans';
import { styled } from '@/UI/theme.exports';
import { Accordion, AccordionDetails, AccordionSummary } from '@/UI/Accordion/Accordion';
import { Box } from '@/UI/Box/Box';
import { Link } from '@/UI/Link/Link';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { MinusIcon } from '@/UI/Icons/MinusIcon';
import { PlusIcon } from '@/UI/Icons/PlusIcon';

const StyledAccordion = styled(Accordion)({
  boxShadow: 'none',
  backgroundColor: 'transparent',
  backgroundImage: 'none',
  borderTop: `1px solid ${token.border}`,
  margin: 0,
  '&.MuiAccordion-root.Mui-expanded': {
    margin: '0 !important',
  },
});

const StyledAccordionSummary = styled(AccordionSummary)({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: token.text,
});

const StyledAccordionDetails = styled(AccordionDetails)({
  fontSize: '0.875rem',
  fontWeight: 400,
  color: token.text,
  lineHeight: '1.25rem',
});

interface PricingFAQProps {
  question: string;
  answer: string;
  link?: {
    href: string;
    text: string;
  };
}

function PricingFAQ({ question, answer, link }: PricingFAQProps) {
  const [expanded, setExpanded] = useState(false);
  const { i18n } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const linkComponent = useMemo(() => {
    if (!link) return undefined;
    return (
      <Link href={link.href} target="_blank" rel="noopener noreferrer">
        {link.text}
      </Link>
    );
  }, [link]);

  const answerContent = linkComponent ? (
    <Trans
      i18nKey={answer}
      components={{
        link: linkComponent,
      }}
    />
  ) : (
    <Trans
      i18nKey={answer}
      components={{
        p: <Box component="p" sx={{ mt: 0, mb: 2, '&:last-child': { mb: 0 } }} />,
        ul: <ul />,
        li: <li />,
        strong: <strong />,
      }}
    />
  );

  return (
    <Box sx={isFplMigrationEnabled ? { borderTop: `1px solid ${token.border}` } : undefined}>
      <StyledAccordion
        expanded={expanded}
        onChange={handleChange}
        TransitionProps={{ unmountOnExit: true }}
        key={`${answer}-${i18n.language}`}
      >
        <StyledAccordionSummary
          expandIcon={expanded ? <MinusIcon width={16} height={16} /> : <PlusIcon width={16} height={16} />}
          aria-controls="faq-content"
          sx={{ height: '64px !important' }}
        >
          {question}
        </StyledAccordionSummary>
        <StyledAccordionDetails sx={{ pt: 0, pb: 3, px: 2 }}>
          {isFplMigrationEnabled ? (
            <Box
              sx={{
                px: 2,
                pb: 3,
                color: token.text,
                fontSize: '0.875rem',
                fontWeight: 400,
                lineHeight: '1.25rem',
              }}
            >
              {answerContent}
            </Box>
          ) : (
            answerContent
          )}
        </StyledAccordionDetails>
      </StyledAccordion>
    </Box>
  );
}

export default PricingFAQ;
